import React from "react";

import { cn } from "@/lib/utils";
import AuthBackdrop from "../AuthBackdrop";
import AuthControls from "../AuthControls";
import packageJson from "../../../../package.json";

/**
 * Shared frame for the public auth screens (sign in, sign up, password reset,
 * activation). Keeps the backdrop, the language/theme controls and the card
 * geometry in one place so the screens cannot drift apart visually.
 */
const AuthShell = ({ children, width = "sm", showVersion = true, className }) => (
  <AuthBackdrop>
    <div className="flex min-h-screen flex-col">
      <header className="flex items-center justify-end p-5">
        <AuthControls />
      </header>

      <main className="flex flex-1 items-center justify-center px-4 pb-12">
        <div
          className={cn(
            "w-full animate-fade-in-up",
            width === "sm" && "max-w-[26rem]",
            width === "md" && "max-w-[34rem]",
            width === "lg" && "max-w-[48rem]",
            className
          )}
        >
          {/* The card should read as paper resting on the backdrop rather than
              as a boxed-in panel — hence no border and a wide shadow.
              One shadow was not enough to carry that, though: in light mode
              `--card` and `--background` are both pure white, so the shadow is
              the *only* thing separating the card from the page, and a single
              soft layer at 18% left it floating flat against it. Three layers
              do the work a real one does — a tight contact shadow at the edge,
              a short mid layer for the lift, and the wide diffuse one for the
              distance — plus a hairline ring that gives the edge somewhere to
              land without reading as a border. */}
          <div className="rounded-3xl bg-card/95 p-8 shadow-[0_0_0_1px_rgba(16,20,40,0.04),0_2px_4px_-1px_rgba(16,20,40,0.06),0_12px_24px_-8px_rgba(16,20,40,0.12),0_32px_64px_-24px_rgba(16,20,40,0.28)] backdrop-blur-2xl sm:p-12 dark:bg-card/75 dark:shadow-[0_0_0_1px_rgba(255,255,255,0.06),0_2px_4px_-1px_rgba(0,0,0,0.4),0_12px_24px_-8px_rgba(0,0,0,0.5),0_32px_64px_-24px_rgba(0,0,0,0.75)]">
            {children}
          </div>

          {showVersion && (
            <p className="mt-6 text-center text-xs text-muted-foreground">
              v{packageJson.version}
            </p>
          )}
        </div>
      </main>
    </div>
  </AuthBackdrop>
);

/** Logo + optional title, the header every auth card starts with. */
export const AuthHeading = ({ title, description, children }) => (
  <div className="flex flex-col items-center gap-4">
    {/* The v2 wordmark. The auth screens were on the old logo, which uses a
        different gradient from the redesigned sidebar's, so the first and
        second thing a user saw did not agree. This is the v2 file with the
        circular mark cropped off at the transparent gutter that separates the
        two, so the gradient matches while the card keeps a single, calm
        wordmark. Width and height carry the crop's own ratio, so reserving the
        box does not shift the heading when the image lands. */}
    <img
      src="/img/logo-boxit-v2-text.png"
      alt="Boxit"
      height="44"
      width="139"
      className="h-11 w-auto"
    />
    {children}
    {title && (
      <h1 className="text-center text-lg font-semibold tracking-tight text-foreground">
        {title}
      </h1>
    )}
    {description && (
      <p className="text-center text-sm text-muted-foreground">
        {description}
      </p>
    )}
  </div>
);

export default AuthShell;
